/**
 * Teks penjelasan retensi di `/admin/ai-assistant/umum` (kartu "Batas pemakaian",
 * grup "Riwayat"). Ada DUA aturan penghapusan yang berjalan bersamaan:
 * isi transkrip (batas server) dan percakapan tidak aktif (field `retentionDays`).
 */

export interface AssistantRetentionText {
	title: string;
	intro: string;
	/** Aturan 1: isi percakapan dihapus N hari sejak pesan dibuat (batas server). */
	transcriptRule: (days: number) => string;
	/** Aturan 2: percakapan tidak aktif dihapus M hari sejak aktivitas terakhir. */
	inactiveRule: (days: number) => string;
	/** Aturan 2 dimatikan (field diisi 0). */
	inactiveRuleOff: string;
	/** Akibat bila aturan 1 lebih ketat: isi sudah hilang di hari ke-N. */
	transcriptFirst: (days: number) => string;
	/** Akibat bila aturan 2 lebih ketat: percakapan utuh hilang lebih dulu. */
	inactiveFirst: (days: number) => string;
}

const id: AssistantRetentionText = {
	title: "Dua aturan penghapusan, keduanya jalan",
	intro:
		"Riwayat percakapan Jenna dihapus oleh dua aturan yang berbeda. Keduanya aktif bersamaan, dan yang paling ketat yang menentukan apa yang masih bisa dibaca:",
	transcriptRule: (days) =>
		`Isi percakapan (transkrip) dihapus permanen ${days} hari setelah pesan dibuat. Batas ini diatur di server lewat env ASSISTANT_TRANSCRIPT_RETENTION_DAYS dan TIDAK bisa diubah dari halaman ini.`,
	inactiveRule: (days) =>
		`Percakapan yang tidak aktif dihapus ${days} hari setelah aktivitas terakhirnya. Angka itu berasal dari kolom "Simpan riwayat (hari)" di atas — inilah satu-satunya aturan yang bisa Anda ubah di sini.`,
	inactiveRuleOff:
		'Percakapan yang tidak aktif tidak pernah dihapus karena kolom "Simpan riwayat (hari)" diisi 0 (simpan selamanya).',
	transcriptFirst: (days) =>
		`Karena aturan pertama lebih ketat, isi percakapan sudah hilang pada hari ke-${days} walaupun kolom "Simpan riwayat (hari)" diisi lebih besar. Setelah itu yang tersisa hanya angka pemakaian & biaya, bukan isinya.`,
	inactiveFirst: (days) =>
		`Karena aturan kedua lebih ketat, percakapan yang tidak aktif sudah terhapus seluruhnya pada hari ke-${days} — sebelum batas transkrip sempat berlaku.`,
};

const en: AssistantRetentionText = {
	title: "Two deletion rules, both active",
	intro:
		"Jenna's conversation history is deleted by two different rules. Both run at the same time, and the stricter one decides what can still be read:",
	transcriptRule: (days) =>
		`Conversation content (the transcript) is permanently deleted ${days} days after a message is created. This limit is set on the server via the ASSISTANT_TRANSCRIPT_RETENTION_DAYS env var and CANNOT be changed from this page.`,
	inactiveRule: (days) =>
		`Inactive conversations are deleted ${days} days after their last activity. That number comes from the "Simpan riwayat (hari)" field above — the only rule you can change here.`,
	inactiveRuleOff:
		'Inactive conversations are never deleted because the "Simpan riwayat (hari)" field is set to 0 (keep forever).',
	transcriptFirst: (days) =>
		`Because the first rule is stricter, conversation content is already gone on day ${days} even if the "Simpan riwayat (hari)" field holds a larger number. After that only usage and cost figures remain, not the content.`,
	inactiveFirst: (days) =>
		`Because the second rule is stricter, inactive conversations are already deleted in full on day ${days} — before the transcript limit ever applies.`,
};

export const assistantRetentionTexts = { id, en } as const;
